import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { FunnelIcon } from '@heroicons/react/24/outline'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useTableData } from 'hooks/useTableData'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'

// GET request for picking summary data
const getPickingSummaryData = async (farmId, dateRange) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/picking-summary?${buildDashboardDateRangeQuery(dateRange)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching picking summary data:', error)
    throw error
  }
}

const themeColorMap = (shade) => {
  return {
    700: 'bg-blue-700',
    600: 'bg-blue-600',
    500: 'bg-blue-500',
    400: 'bg-blue-400'
  }[shade]
}

const DailyPickSummary = () => {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const filterOptions = useDashboardFilterOptions(user?.farmId)

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')

  const [dateRange, setDateRange] = useDashboardDateRange(currentDateForPicker)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)

  // State for API data
  const [pickingSummaryData, setPickingSummaryData] = useState({
    dailyPickingSummary: {
      totalNetPicked: 0,
      pickerCount: 0,
      averagePerWorker: 0,
      averageUnitsPerHour: 0
    },
    dailyPickingSummarySameHourPreviousDay: {
      totalNetPicked: 0,
      pickerCount: 0,
      averagePerWorker: 0,
      averageUnitsPerHour: 0
    },
    pickerGroupsMonitor: [],
    pickersMonitor: [],
    blockMonitor: [],
    loading: true,
    error: null
  })

  const fetchPickingSummaryData = async (rangeToFetch = dateRange) => {
    if (!user?.farmId) return

    try {
      setPickingSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getPickingSummaryData(user.farmId, rangeToFetch)

      setPickingSummaryData(prev => ({
        ...prev,
        dailyPickingSummary: data.dailyPickingSummary || prev.dailyPickingSummary,
        dailyPickingSummarySameHourPreviousDay: data.dailyPickingSummarySameHourPreviousDay || prev.dailyPickingSummarySameHourPreviousDay,
        pickerGroupsMonitor: data.pickerGroupsMonitor || [],
        pickersMonitor: data.pickersMonitor || [],
        blockMonitor: data.blockMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching picking summary:', error)
      setPickingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load picking summary data'
      }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    fetchPickingSummaryData()
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchPickingSummaryData(dateRange)
  }, [dateRange])

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPickingSummary, dailyPickingSummarySameHourPreviousDay } = pickingSummaryData
    const selectedRangeFormatted = getDateRangeSummary(dateRange)

    return [
      {
        title: 'Total Net Picked',
        value: dailyPickingSummary.totalNetPicked?.toLocaleString() || '0',
        unit: 'Units',
        sub: `Picking Range (${selectedRangeFormatted})`,
        color: 'bg-blue-700'
      },
      {
        title: 'Total Net Picked',
        value: dailyPickingSummarySameHourPreviousDay.totalNetPicked?.toLocaleString() || '0',
        unit: 'Units',
        sub: 'Same Hour On Previous Picking Day',
        color: themeColorMap(500)
      },
      {
        title: 'Pickers Count',
        value: dailyPickingSummary.pickerCount?.toString() || '0',
        unit: 'Workers',
        sub: `Picking Range (${selectedRangeFormatted})`,
        color: 'bg-blue-700'
      },
      {
        title: 'Pickers Count',
        value: dailyPickingSummarySameHourPreviousDay.pickerCount?.toString() || '0',
        unit: 'Workers',
        sub: 'Same Hour On Previous Picking Day',
        color: themeColorMap(500)
      },
      {
        title: 'Avg Per Picker',
        value: dailyPickingSummary.averagePerWorker?.toFixed(2) || '0',
        unit: 'Units',
        sub: `Picking Range (${selectedRangeFormatted})`,
        color: 'bg-blue-600'
      },
      {
        title: 'Avg Per Picker',
        value: dailyPickingSummarySameHourPreviousDay.averagePerWorker?.toFixed(2) || '0',
        unit: 'Units',
        sub: 'Same Hour On Previous Picking Day',
        color: themeColorMap(400)
      },
      {
        title: 'Worker Avg',
        value: dailyPickingSummary.averageUnitsPerHour?.toFixed(2) || '0',
        unit: 'Units/Hour',
        sub: `Picking Range (${selectedRangeFormatted})`,
        color: 'bg-blue-700'
      },
      {
        title: 'Worker Avg',
        value: dailyPickingSummarySameHourPreviousDay.averageUnitsPerHour?.toFixed(2) || '0',
        unit: 'Units/Hour',
        sub: 'Same Hour On Previous Picking Day',
        color: themeColorMap(500)
      }
    ]
  }

  // Use the custom hook to generate table data with totals
  const pickerGroupsTableData = useTableData(pickingSummaryData.pickerGroupsMonitor, 'pickerGroups')
  const pickerMonitorTableData = useTableData(pickingSummaryData.pickersMonitor, 'pickerMonitor')
  const blockMonitorTableData = useTableData(pickingSummaryData.blockMonitor, 'blockMonitor')

  if (pickingSummaryData.loading) {
    return <PageLoadingState message='Loading picking summary...' rows={12} />
  }

  if (pickingSummaryData.error) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>{pickingSummaryData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>

      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-blue-600'>Daily Pick Summary</h1>
          <p className='text-sm text-gray-600'>
            Change Filters
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>Advanced filters</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      <StatCards cards={createCardsData()} />

      <MonitorCard title='Picker Groups Monitor' date={getDateRangeSummary(dateRange)} hasData={pickerGroupsTableData.rows.length > 0}>
        <TableComponent tableData={pickerGroupsTableData} defaultSortColumn={pickerGroupsTableData.columns[0]?.key} />
      </MonitorCard>

      <MonitorCard title='Picker Monitor' date={getDateRangeSummary(dateRange)} hasData={pickerMonitorTableData.rows.length > 0}>
        <TableComponent tableData={pickerMonitorTableData} defaultSortColumn={pickerMonitorTableData.columns[0]?.key} />
      </MonitorCard>

      <MonitorCard title='Block Monitor' date={getDateRangeSummary(dateRange)} hasData={blockMonitorTableData.rows.length > 0}>
        <TableComponent tableData={blockMonitorTableData} defaultSortColumn={blockMonitorTableData.columns[0]?.key} />
      </MonitorCard>

      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={currentDateForPicker}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          setDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default DailyPickSummary
